import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import React from "react";

export const ContextMenuRoot = ContextMenuPrimitive.Root;
export const ContextMenuTrigger = ContextMenuPrimitive.Trigger;
export const ContextMenuPortal = ContextMenuPrimitive.Portal;

export const ContextMenuSeparator = React.forwardRef<
    React.ElementRef<typeof ContextMenuPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Separator> & PropsWithClassName
>(({ className, ...props }, ref) => (
    <ContextMenuPrimitive.Separator
        ref={ref}
        className={cn("my-1 h-px bg-border-base bg-content-tertiary/20", className)}
        {...props}
    />
));
ContextMenuSeparator.displayName = ContextMenuPrimitive.ContextMenuItem.displayName;

export const ContextMenuContent = React.forwardRef<
    React.ElementRef<typeof ContextMenuPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Content> & PropsWithClassName
>(({ className, ...props }, ref) => (
    <ContextMenuPrimitive.Content
        ref={ref}
        className={cn(
            "z-50", // z-index is consistent with our dropdown menus
            "min-w-[220px] rounded-md bg-surface-01 p-1 shadow-lg",
            "w-64 rounded-lg border border-border-base bg-surface-popover p-0 shadow first:pt-1 last:pb-1",
            className,
        )}
        {...props}
    />
));
ContextMenuContent.displayName = ContextMenuPrimitive.ContextMenuItem.displayName;

import { cva } from "class-variance-authority";
import type { BaseIconProps } from "@/assets/icons/utils";

const contextMenuItemVariants = cva(
    cn(
        "flex cursor-pointer items-center outline-none rounded text-base text-content-primary px-2 py-2 h-8",
        "hover:bg-surface-hover focus:bg-surface-hover",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
    ),
    {
        variants: {
            variant: {
                default: "",
                destructive: "text-content-destructive",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    },
);

export const ContextMenuItem = React.forwardRef<
    React.ElementRef<typeof ContextMenuPrimitive.ContextMenuItem>,
    React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.ContextMenuItem> &
        PropsWithClassName & { variant?: "default" | "destructive"; LeadingIcon?: React.ComponentType<BaseIconProps> }
>(({ onClick, className, variant, LeadingIcon, children, ...props }, ref) => (
    <ContextMenuPrimitive.ContextMenuItem
        ref={ref}
        className={cn(contextMenuItemVariants({ variant }), className)}
        onClick={(e) => {
            if (onClick) {
                e.stopPropagation();
                // We're using setTimeout to allow the context menu to clear its modification to body.style before triggering the onclick,
                // This is important if the onClick handler opens a modal, as otherwise the modal will reset the style when the modal is
                // closed, which causes the pointer-events:none to stick around.
                setTimeout(() => onClick(e), 0);
            }
        }}
        {...props}
    >
        {LeadingIcon ? (
            <span className="mr-2">
                <LeadingIcon size="sm" />
            </span>
        ) : null}
        {children}
    </ContextMenuPrimitive.ContextMenuItem>
));
ContextMenuItem.displayName = ContextMenuPrimitive.ContextMenuItem.displayName;
